import { Fragment, useRef, useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  Zap,
  ImagePlus,
  ArrowUpDown,
  Layers,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { useUploadDepartmentImage } from '@/hooks/use-category-department-image';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { useLocationStore } from '@/stores/location-store';
import { PageHeader } from '@/components/shared/page-header';
import { ReorderListDialog } from '@/components/pos-management/reorder-list-dialog';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
  useApplyDepartmentDefaults,
  useDepartmentProductCount,
} from '@/hooks/use-departments';
import { toast } from '@/stores/toast-store';
import { formatCrvOption } from '@/lib/constants';
import { normalizeCatalogName } from '@/lib/product-xlsx';
import { useCrvOptions } from '@/hooks/use-crv-options';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/** Thumbnail + upload button for a department's POS tile image. stopPropagation
 *  so clicking it doesn't toggle the expandable row. */
function DepartmentImageCell({ dept, readOnly }: { dept: any; readOnly: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadDepartmentImage();
  const uploadEnabled = useAuthStore((s) => s.tenant?.allowCategoryDepartmentImageUpload === true);
  // Super-admin gated. When off, show an existing image read-only (no upload
  // control) and hide the cell entirely when there's nothing to show.
  if (!uploadEnabled || readOnly) {
    return dept.imageUrl ? (
      <img
        src={dept.imageUrl}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="h-8 w-8 rounded-md border border-border object-cover"
      />
    ) : null;
  }
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {dept.imageUrl ? (
        <img src={dept.imageUrl} alt="" className="h-8 w-8 rounded-md border border-border object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
          <ImagePlus className="h-4 w-4" />
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            await upload.mutateAsync({ departmentId: dept.id, file });
            toast.success('Image uploaded — POS updates on next sync');
          } catch {
            toast.error('Image upload failed');
          }
        }}
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
        disabled={upload.isPending}
        className="text-xs text-primary hover:underline disabled:opacity-50"
      >
        {upload.isPending ? 'Uploading…' : dept.imageUrl ? 'Replace' : 'Image'}
      </button>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-gray-200'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform shadow ${checked ? 'translate-x-4' : ''}`}
      />
    </button>
  );
}

export function DepartmentListPage() {
  const { data, isLoading } = useDepartments();
  const departments = data?.data ?? [];

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // Show in the POS display order (sort_order), then alphabetical.
  const sortedDepartments = [...departments].sort(
    (a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const total = sortedDepartments.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const paginatedDepartments = sortedDepartments.slice((page - 1) * perPage, page * perPage);

  const createDept = useCreateDepartment();
  const updateDept = useUpdateDepartment();
  // Taxable / Coupon / CRV / Tax Rate defaults cascade to this store's
  // products only (they are per store); the other defaults stay product-wide.
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const deleteDept = useDeleteDepartment();
  const permissions = usePermissions();
  const canAdd = permissions.has('departments.add');
  const canEdit = permissions.has('departments.edit');
  const canDelete = permissions.has('departments.delete');
  const applyDefaults = useApplyDepartmentDefaults();
  const [applyTarget, setApplyTarget] = useState<{ id: string; name: string } | null>(null);
  const applyCountQuery = useDepartmentProductCount(applyTarget?.id ?? null);
  const applyCount = applyCountQuery.data?.data.count ?? 0;

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; linkedCount: number } | null>(null);

  // Tenant-configurable CRV fee options (falls back to defaults while loading).
  const { options: crvOptions } = useCrvOptions();

  const [showCreate, setShowCreate] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Persist the dragged order: each department's sort_order = its new index.
  const handleSaveOrder = async (orderedIds: string[]) => {
    setSavingOrder(true);
    try {
      await Promise.all(orderedIds.map((id, i) => updateDept.mutateAsync({ id, sortOrder: i })));
      toast.success('Department order saved \u2014 POS devices update on next sync');
      setShowReorder(false);
    } catch {
      toast.error('Failed to save department order');
    } finally {
      setSavingOrder(false);
    }
  };
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formDefaults, setFormDefaults] = useState({
    defaultTaxable: true,
    defaultEbtEligible: true,
    defaultCouponEligible: true,
    defaultAgeVerification: false,
    defaultExcludeKitchenPrint: false,
    defaultSoldByWeight: false,
    defaultPromptQuantity: false,
    defaultIsVariablePrice: false,
    defaultCrvFee: 0,
    defaultTaxRate: '',
  });

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetForm = () => {
    setFormName('');
    setFormDesc('');
    setFormDefaults({
      defaultTaxable: true,
      defaultEbtEligible: true,
      defaultCouponEligible: true,
      defaultAgeVerification: false,
      defaultExcludeKitchenPrint: false,
      defaultSoldByWeight: false,
      defaultPromptQuantity: false,
      defaultIsVariablePrice: false,
      defaultCrvFee: 0,
      defaultTaxRate: '',
    });
  };

  const handleCreate = async () => {
    if (!normalizeCatalogName(formName)) return;
    await createDept.mutateAsync({
      name: normalizeCatalogName(formName),
      description: formDesc,
      ...formDefaults,
      defaultTaxRate: formDefaults.defaultTaxRate ? parseFloat(formDefaults.defaultTaxRate) : undefined,
    });
    resetForm();
    setShowCreate(false);
  };

  const startEdit = (dept: any) => {
    setEditingId(dept.id);
    setFormName(dept.name);
    setFormDesc(dept.description || '');
    setFormDefaults({
      defaultTaxable: dept.defaultTaxable ?? true,
      defaultEbtEligible: dept.defaultEbtEligible ?? true,
      defaultCouponEligible: dept.defaultCouponEligible ?? true,
      defaultAgeVerification: dept.defaultAgeVerification ?? false,
      defaultExcludeKitchenPrint: dept.defaultExcludeKitchenPrint ?? false,
      defaultSoldByWeight: dept.defaultSoldByWeight ?? false,
      defaultPromptQuantity: dept.defaultPromptQuantity ?? false,
      defaultIsVariablePrice: dept.defaultIsVariablePrice ?? false,
      defaultCrvFee: dept.defaultCrvFee ?? 0,
      defaultTaxRate: dept.defaultTaxRate?.toString() || '',
    });
  };

  const handleUpdate = async () => {
    if (!normalizeCatalogName(formName) || !editingId) return;
    await updateDept.mutateAsync({
      id: editingId,
      name: normalizeCatalogName(formName),
      description: formDesc,
      ...formDefaults,
      defaultTaxRate: formDefaults.defaultTaxRate ? parseFloat(formDefaults.defaultTaxRate) : null,
      locationId: selectedLocationId || undefined,
    });
    setEditingId(null);
    resetForm();
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteDept.mutateAsync({ id });
      toast.success(`Deleted department "${name}"`);
    } catch (e: any) {
      if (e?.error?.code === 'DEPARTMENT_HAS_PRODUCTS') {
        setDeleteTarget({ id, name, linkedCount: e.error.linkedProductCount ?? 0 });
        return;
      }
      toast.error(e?.error?.message || `Failed to delete "${name}"`);
    }
  };

  const handleConfirmCascadeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDept.mutateAsync({ id: deleteTarget.id, cascade: true });
      toast.success(
        `Deleted "${deleteTarget.name}" and unassigned ${deleteTarget.linkedCount} product${
          deleteTarget.linkedCount === 1 ? '' : 's'
        }`,
      );
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.error?.message || 'Failed to delete department');
    }
  };

  const handleApplyDefaults = async () => {
    if (!applyTarget) return;
    try {
      const res = await applyDefaults.mutateAsync({ id: applyTarget.id, locationId: selectedLocationId });
      toast.success(`Updated ${res.data.updated} product${res.data.updated === 1 ? '' : 's'} in "${applyTarget.name}"`);
      setApplyTarget(null);
    } catch (e) {
      toast.error(`Failed to apply defaults: ${e instanceof Error ? e.message : 'Unknown error'}`);
    }
  };

  const defaultTogglesJsx = (
    <div className="mt-3 space-y-2 rounded-lg border border-border bg-muted/20 p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Default toggles for new items in this department:
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {[
          { key: 'defaultTaxable' as const, label: 'Taxable' },
          { key: 'defaultEbtEligible' as const, label: 'EBT Eligible' },
          { key: 'defaultCouponEligible' as const, label: 'Coupon Eligible' },
          { key: 'defaultAgeVerification' as const, label: 'Age Verification' },
          { key: 'defaultExcludeKitchenPrint' as const, label: 'Kitchen Print' },
          { key: 'defaultSoldByWeight' as const, label: 'Sold By Weight' },
          { key: 'defaultPromptQuantity' as const, label: 'Prompt Quantity' },
          { key: 'defaultIsVariablePrice' as const, label: 'Variable Price' },
        ].map(({ key, label }) => (
          <label key={key} className="flex items-center justify-between text-xs">
            <span>{label}</span>
            <Toggle
              checked={formDefaults[key] as boolean}
              onChange={(v) => setFormDefaults((d) => ({ ...d, [key]: v }))}
            />
          </label>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4">
        <div>
          <label className="block text-xs text-muted-foreground">Default Tax Rate (%)</label>
          <input
            type="number"
            step="0.01"
            value={formDefaults.defaultTaxRate}
            onChange={(e) => setFormDefaults((d) => ({ ...d, defaultTaxRate: e.target.value }))}
            placeholder="Use store default"
            className="mt-1 h-7 w-32 rounded border border-input px-2 text-xs outline-none focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground">Default CRV Fee</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {crvOptions.map((cents) => (
              <button
                key={cents}
                type="button"
                onClick={() => setFormDefaults((d) => ({ ...d, defaultCrvFee: cents }))}
                className={`rounded px-2.5 py-1 text-xs font-medium border transition-colors ${
                  formDefaults.defaultCrvFee === cents
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white text-foreground border-border hover:bg-muted'
                }`}
              >
                {formatCrvOption(cents)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <PageHeader title="Departments">
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setShowReorder(true)}
              disabled={sortedDepartments.length < 2}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
            >
              <ArrowUpDown className="h-4 w-4" /> Reorder
            </button>
          )}
          {canAdd && (
            <button
              onClick={() => {
                setShowCreate(true);
                resetForm();
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> Create
            </button>
          )}
        </div>
      </PageHeader>

      <ReorderListDialog
        open={showReorder}
        onClose={() => setShowReorder(false)}
        title="Reorder departments"
        items={sortedDepartments.map((d: any) => ({ id: d.id, name: d.name }))}
        onSave={handleSaveOrder}
        saving={savingOrder}
      />

      <div className="rounded-lg border border-border bg-white">
        {showCreate && (
          <div className="border-b border-border px-4 py-3 bg-muted/30">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Department Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
                className="h-9 flex-1 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                className="h-9 flex-1 rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={handleCreate}
                disabled={createDept.isPending}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {defaultTogglesJsx}
          </div>
        )}

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : departments.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No departments yet"
            description="Click Create to add your first department."
          />
        ) : (
          <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[560px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="w-10 px-3 py-3.5" />
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Name
                  </th>
                  <th className="px-5 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700 uppercase">
                    Description
                  </th>
                  <th className="px-5 py-3.5 text-right text-[13px] font-semibold tracking-wide text-gray-700 uppercase w-28">
                    Products
                  </th>
                  <th className="px-5 py-3.5 text-right text-[13px] font-semibold tracking-wide text-gray-700 uppercase w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedDepartments.map((dept: any) => (
                  <Fragment key={dept.id}>
                    <tr
                      className={`border-b border-border hover:bg-muted/30 transition-colors ${editingId !== dept.id ? 'cursor-pointer' : ''}`}
                      onClick={() => editingId !== dept.id && toggleRow(dept.id)}
                    >
                      {editingId === dept.id ? (
                        <td colSpan={5} className="px-4 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              type="text"
                              value={formName}
                              onChange={(e) => setFormName(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
                              autoFocus
                              className="h-9 min-w-0 flex-1 rounded border border-input px-2 text-sm outline-none focus:border-primary"
                            />
                            <input
                              type="text"
                              value={formDesc}
                              onChange={(e) => setFormDesc(e.target.value)}
                              className="h-9 min-w-0 flex-1 rounded border border-input px-2 text-sm outline-none focus:border-primary"
                            />
                            <button
                              onClick={handleUpdate}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border hover:bg-muted"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingId(null);
                              }}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border hover:bg-muted"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {defaultTogglesJsx}
                        </td>
                      ) : (
                        <>
                          <td className="px-3 py-3">
                            <button className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted">
                              {expandedRows.has(dept.id) ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{dept.name}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{dept.description || '—'}</td>
                          <td
                            className="px-4 py-3 text-right text-sm tabular-nums"
                            aria-label={`${dept.productCount ?? 0} products`}
                          >
                            {dept.productCount ?? 0}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <DepartmentImageCell dept={dept} readOnly={!canEdit} />
                              {canEdit && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(dept);
                                  }}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted"
                                  title="Edit department"
                                  aria-label="Edit department"
                                >
                                  <Pencil className="h-4 w-4 text-primary" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(dept.id, dept.name);
                                  }}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                                  title="Delete department"
                                  aria-label="Delete department"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                    {expandedRows.has(dept.id) && editingId !== dept.id && (
                      <tr className="border-b border-border">
                        <td colSpan={5} className="bg-muted/20 px-6 py-4">
                          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs sm:grid-cols-3 lg:grid-cols-4">
                            {[
                              { label: 'Taxable', val: dept.defaultTaxable !== false },
                              { label: 'EBT Eligible', val: dept.defaultEbtEligible !== false },
                              { label: 'Coupon Eligible', val: dept.defaultCouponEligible !== false },
                              { label: 'Age Verify', val: dept.defaultAgeVerification },
                              { label: 'Kitchen Print', val: dept.defaultExcludeKitchenPrint },
                              { label: 'Sold By Weight', val: dept.defaultSoldByWeight },
                              { label: 'Prompt Qty', val: dept.defaultPromptQuantity },
                              { label: 'Variable Price', val: dept.defaultIsVariablePrice },
                            ].map(({ label, val }) => (
                              <div key={label} className="flex items-center gap-1.5">
                                <span className="text-muted-foreground">{label}:</span>
                                <span className={`font-medium ${val ? 'text-green-600' : 'text-red-500'}`}>
                                  {val ? 'Yes' : 'No'}
                                </span>
                              </div>
                            ))}
                            {dept.defaultCrvFee > 0 && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-muted-foreground">CRV Fee:</span>
                                <span className="font-medium">{formatCrvOption(dept.defaultCrvFee)}</span>
                              </div>
                            )}
                            {dept.defaultTaxRate && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-muted-foreground">Tax Rate:</span>
                                <span className="font-medium">{dept.defaultTaxRate}%</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
                            <p className="text-[11px] text-muted-foreground">
                              Defaults apply to new products. Existing products retain their own toggles unless
                              reconciled.
                            </p>
                            {canEdit && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setApplyTarget({ id: dept.id, name: dept.name });
                                }}
                                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
                              >
                                <Zap className="h-3 w-3" />
                                Apply to existing products
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!isLoading && departments.length > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={handlePerPageChange}
        />
      )}
      <ConfirmDialog
        open={applyTarget !== null}
        title="Apply defaults to existing products?"
        message={
          applyCountQuery.isLoading
            ? `Counting products in "${applyTarget?.name}"...`
            : applyCount === 0
              ? `"${applyTarget?.name}" has no products. Nothing to update.`
              : `This will overwrite EBT Eligible, Age Verification, Kitchen Print, Sold By Weight, Prompt Quantity and Variable Price for all ${applyCount} product${applyCount === 1 ? '' : 's'} currently in "${applyTarget?.name}", and Taxable, Coupon Eligible, CRV Fee and Tax Rate ${selectedLocationId ? `at ${selectedLocationName}` : 'at every location'}. Individual product overrides will be lost.`
        }
        confirmLabel={applyCount === 0 ? 'OK' : `Apply to ${applyCount}`}
        variant="warning"
        loading={applyDefaults.isPending}
        onConfirm={applyCount === 0 ? () => setApplyTarget(null) : handleApplyDefaults}
        onCancel={() => setApplyTarget(null)}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Unassign products and delete "${deleteTarget?.name}"?`}
        message={
          deleteTarget
            ? `${deleteTarget.linkedCount} product${deleteTarget.linkedCount === 1 ? ' is' : 's are'} still linked to this department. Continuing will clear the department on ${deleteTarget.linkedCount === 1 ? 'that product' : 'those products'} and then delete the department. This cannot be undone.`
            : ''
        }
        confirmLabel={`Unassign & delete`}
        variant="danger"
        loading={deleteDept.isPending}
        onConfirm={handleConfirmCascadeDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
